import React, { useEffect, useState, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { BottomNav } from './components/BottomNav';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TravelModeProvider } from './contexts/TravelModeContext';
import { LoadingScreen } from './components/LoadingScreen';
import { AuthOverlay } from './components/AuthOverlay';

console.log("VITE_APP_MODE:", import.meta.env.VITE_APP_MODE);

// Lazy load pages with proper error boundaries
const Home = React.lazy(() => import('./pages/Home'));
const Search = React.lazy(() => import('./pages/Search'));
const Submit = React.lazy(() => import('./pages/Submit'));
const Favorites = React.lazy(() => import('./pages/Favorites'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Login = React.lazy(() => import('./pages/Login'));
const ListingDetail = React.lazy(() => import('./pages/ListingDetail'));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage'));
const Categories = React.lazy(() => import('./pages/Categories'));
const DebugAuth = React.lazy(() => import('./pages/DebugAuth'));
const BusinessLanding = React.lazy(() => import('./pages/static/BusinessLanding'));
const BusinessTierGuide = React.lazy(() => import('./pages/BusinessTierGuide'));
const WriteReview = React.lazy(() => import('./pages/WriteReview'));

// Onboarding pages
const Welcome = React.lazy(() => import('./pages/onboarding/Welcome'));
const AllergyPreferences = React.lazy(() => import('./pages/onboarding/AllergyPreferences'));
const Location = React.lazy(() => import('./pages/onboarding/Location'));
const Signup = React.lazy(() => import('./pages/onboarding/Signup'));
const Success = React.lazy(() => import('./pages/onboarding/Success'));

// Static pages
const OurStory = React.lazy(() => import('./pages/static/OurStory'));
const Contact = React.lazy(() => import('./pages/static/Contact'));
const Terms = React.lazy(() => import('./pages/static/Terms'));
const Privacy = React.lazy(() => import('./pages/static/Privacy'));
const Disclaimer = React.lazy(() => import('./pages/static/Disclaimer'));
const Partner = React.lazy(() => import('./pages/static/Partner'));
const Verified = React.lazy(() => import('./pages/static/Verified'));
const VerificationForm = React.lazy(() => import('./pages/static/VerificationForm'));
const Report = React.lazy(() => import('./pages/static/Report'));
const JoinTeam = React.lazy(() => import('./pages/static/JoinTeam'));
const AmbassadorApplication = React.lazy(() => import('./pages/static/AmbassadorApplication'));
const BusinessTiers = React.lazy(() => import('./pages/static/BusinessTiers'));

// Inspector pages
const InspectorDashboard = React.lazy(() => import('./pages/inspector/Dashboard'));
const TeamProfile = React.lazy(() => import('./pages/inspector/TeamProfile'));

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">We're sorry, but there was an error loading this page.</p>
            <button 
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }} 
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Try reloading the page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ScrollToTop component to handle scroll behavior
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Protected route wrapper with immediate redirect
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, initializing, isAuthenticated, authReady, setAuthInProgress } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);
  const [showContent, setShowContent] = useState(false);

  // Immediate redirect if not authenticated
  useEffect(() => {
    if (!initializing && authReady && !isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      setAuthInProgress(true);
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }
    
    // Show content after a short delay to prevent flashing
    if (!initializing && authReady && isAuthenticated) {
      const timer = setTimeout(() => {
        setShowContent(true);
        setAuthInProgress(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [initializing, authReady, isAuthenticated, navigate, location.pathname, setAuthInProgress]);

  // Show loading during auth check
  if (initializing || loading || !authReady || !isAuthenticated || !showContent) {
    return <LoadingScreen />;
  }

  // Only render children when authenticated
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// Public route wrapper with immediate redirect
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { loading, initializing, isAuthenticated, authReady, setAuthInProgress } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);
  const [showContent, setShowContent] = useState(false);

  // Immediate redirect if authenticated
  useEffect(() => {
    if (!initializing && authReady && isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      setAuthInProgress(true);
      navigate('/home', { replace: true });
    }
    
    // Show content after a short delay to prevent flashing
    if (!initializing && authReady && !isAuthenticated) {
      const timer = setTimeout(() => {
        setShowContent(true);
        setAuthInProgress(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [initializing, authReady, isAuthenticated, navigate, setAuthInProgress]);

  // Show loading during auth check
  if (initializing || loading || !authReady || isAuthenticated || !showContent) {
    return <LoadingScreen />;
  }

  // Only render children when not authenticated
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

function AppRoutes() {
  const { isAuthenticated, authReady, initializing, loading } = useAuth();
  const location = useLocation();

  // Show loading screen during initial auth check
  if (initializing || !authReady || loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <ScrollToTop />
      <div className="flex-1 overflow-y-auto scrollbar-hide pb-16">
        <Suspense fallback={<LoadingScreen />}>
          <ErrorBoundary>
            <Routes>
              {/* Root route redirects to home */}
              <Route 
                path="/" 
                element={<Navigate to={isAuthenticated ? "/home" : "/login"} replace />}
              />
              
              {/* Protected routes */}
              <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
              <Route path="/submit" element={<ProtectedRoute><Submit /></ProtectedRoute>} />
              <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
              <Route path="/profile/*" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/write-review/:listingId" element={<ProtectedRoute><WriteReview /></ProtectedRoute>} />
              
              {/* Public routes */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/business/:slug" element={<BusinessLanding />} />
              <Route path="/:category" element={<CategoryPage />} />
              <Route path="/debug-auth" element={<DebugAuth />} />
              <Route path="/business-tier-guide" element={<BusinessTierGuide />} />

              {/* Onboarding routes */}
              <Route path="/onboarding" element={<PublicRoute><Welcome /></PublicRoute>} />
              <Route path="/onboarding/allergies" element={<PublicRoute><AllergyPreferences /></PublicRoute>} />
              <Route path="/onboarding/location" element={<PublicRoute><Location /></PublicRoute>} />
              <Route path="/onboarding/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/onboarding/success" element={<PublicRoute><Success /></PublicRoute>} />

              {/* Static pages */}
              <Route path="/our-story" element={<ErrorBoundary><OurStory /></ErrorBoundary>} />
              <Route path="/contact" element={<ErrorBoundary><Contact /></ErrorBoundary>} />
              <Route path="/terms" element={<ErrorBoundary><Terms /></ErrorBoundary>} />
              <Route path="/privacy" element={<ErrorBoundary><Privacy /></ErrorBoundary>} />
              <Route path="/disclaimer" element={<ErrorBoundary><Disclaimer /></ErrorBoundary>} />
              <Route path="/partner" element={<ErrorBoundary><Partner /></ErrorBoundary>} />
              <Route path="/verified" element={<ErrorBoundary><Verified /></ErrorBoundary>} />
              <Route path="/verified/apply" element={<ErrorBoundary><VerificationForm /></ErrorBoundary>} />
              <Route path="/report" element={<ErrorBoundary><Report /></ErrorBoundary>} />
              <Route path="/join-team" element={<ErrorBoundary><JoinTeam /></ErrorBoundary>} />
              <Route path="/join-team/apply" element={<ErrorBoundary><AmbassadorApplication /></ErrorBoundary>} />
              <Route path="/business-tiers" element={<ErrorBoundary><BusinessTiers /></ErrorBoundary>} />

              {/* Inspector routes */}
              <Route path="/inspector/dashboard" element={<ProtectedRoute><InspectorDashboard /></ProtectedRoute>} />
              <Route path="/team/:subdomain" element={<ErrorBoundary><TeamProfile /></ErrorBoundary>} />

              {/* Catch all route */}
              <Route path="*" element={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                  <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Page not found</h2>
                    <Link to="/home" className="text-primary-600 hover:text-primary-700 font-medium">
                      Return home
                    </Link>
                  </div>
                </div>
              } />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </div>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <TravelModeProvider>
          <AuthOverlay>
            <AppRoutes />
          </AuthOverlay>
        </TravelModeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;